import { ColumnDef } from "@tanstack/react-table";

/****
 * Checks if the column id is selected in the provided setting value.
 * @param settingValue Serialized JSON string or undefined representing selected columns.
 * @param key Property key in the parsed settings to look up the array of selected column ids.
 * @param id Column id to check for selection.
 * @returns Boolean indicating whether the column id is selected.
 */
export function isColumnSelected(
  settingValue: string | undefined,
  key: string,
  id: string,
): boolean {
  if (!settingValue) return false;
  try {
    const parsed = JSON.parse(settingValue);
    const arr: string[] = Array.isArray(parsed[key]) ? parsed[key] : [];
    return arr.includes(id);
  } catch (e) {
    return false;
  }
}

/**
 * Toggles the explorer column selection for a given key and id by updating settings.
 * @param {string|undefined} settingValue - Current JSON string of settings or undefined.
 * @param {(payload: { name: string; value: string }) => Promise<void>} createUpdateSetting - Function to update settings.
 * @param {string} key - The key of the explorer column group to toggle within.
 * @param {string} id - The id of the column to toggle selection for.
 * @returns {Promise<void>} A promise that resolves when the setting update completes.
 */
export async function toggleExplorerColumn(
  settingValue: string | undefined,
  createUpdateSetting: (payload: {
    name: string;
    userId?: number;
    value: string;
  }) => Promise<void>,
  key: string,
  id: string,
  userId?: number,
) {
  const parsed = settingValue ? JSON.parse(settingValue) : {};
  const current: string[] = Array.isArray(parsed[key]) ? parsed[key] : [];
  const newSelection = current.includes(id)
    ? current.filter((colId: string) => colId !== id)
    : [...current, id];

  await createUpdateSetting({
    name: "explorerColumns",
    userId,
    value: JSON.stringify({ ...parsed, [key]: newSelection }),
  });
}

/**
 * Builds explorer columns based on user settings or defaults.
 *
 * @param settingValue - JSON string of settings or undefined.
 * @param key - Key in parsed settings containing column IDs.
 * @param columns - Function to generate a column object given an ID.
 * @param defaultFirst - Default column ID or IDs to include if missing. Can be a string or an array of strings.
 * @returns An array of column objects for the explorer.
 */
export function buildExplorerColumns<T>(
  settingValue: string | undefined,
  key: string,
  columns: (id: string) => ColumnDef<T>,
  defaultFirst: string | string[] = "name",
) {
  try {
    const defaultsArray = Array.isArray(defaultFirst)
      ? defaultFirst
      : [defaultFirst];

    // helper to map default ids to ColumnDef, preserving order
    const mapDefaults = () =>
      defaultsArray.map((id) => columns(id)).filter(Boolean) as ColumnDef<T>[];

    if (!settingValue) return mapDefaults();

    const parsed = JSON.parse(settingValue);
    const arr: string[] = Array.isArray(parsed[key]) ? parsed[key] : [];

    if (!Array.isArray(arr) || arr.length === 0) {
      return mapDefaults();
    }

    const mapped = arr
      .map((id) => columns(id))
      .filter(Boolean) as ColumnDef<T>[];

    if (!mapped || mapped.length === 0) return mapDefaults();

    // Build result such that specified defaults (in order) are first.
    const result: ColumnDef<T>[] = [];
    const remaining = [...mapped];

    for (const defId of defaultsArray) {
      const idx = remaining.findIndex((c) => c && c.id === defId);
      if (idx > -1) {
        // move existing mapped default to the front (preserve mapped instance)
        const col = remaining.splice(idx, 1)[0];
        result.push(col);
      } else {
        // not in mapped, create default column and add it
        const col = columns(defId);
        if (col) result.push(col as ColumnDef<T>);
      }
    }

    return [...result, ...remaining];
  } catch (e) {
    return Array.isArray(defaultFirst)
      ? (defaultFirst
          .map((id) => columns(id))
          .filter(Boolean) as ColumnDef<T>[])
      : [columns(defaultFirst)];
  }
}
